import { WEEKDAY_MAP } from "@/consts";
import toRRuleByweekday from "@/helpers/rrules/toRRuleByweekday";
import toRRuleFreq from "@/helpers/rrules/toRRuleFreq";
import type { RruleOptions } from "@/types";
import { datetime, RRule, type Options } from "rrule";

type StrategyOptions = {
  useBysetpos: boolean;
};

type ByWeekDayType = Options["byweekday"] | undefined;

const DEFAULT_STRATEGY: StrategyOptions = {
  useBysetpos: false,
};

export default (
  options: RruleOptions,
  strategyOptions: Partial<StrategyOptions> = {},
) => {
  const strategy = { ...DEFAULT_STRATEGY, ...strategyOptions };

  let byweekday: ByWeekDayType = undefined;

  // weekly
  if (options.cadence === 7) {
    byweekday = toRRuleByweekday(options.cadence, options.days);
  }

  // monthly
  if (options.cadence === 30 && options.daySelection !== undefined) {
    const weekdayFinder = WEEKDAY_MAP[options?.daySelection];
    if (weekdayFinder) {
      byweekday = [weekdayFinder.nth(options.dayOrder || 1)];
    }
  }

  const params: Partial<Options> = {
    freq: toRRuleFreq(options.cadence),
    interval: options.occurence,

    wkst: options.cadence === 30 ? options.daySelection : undefined,

    byweekday,

    // Bysetpos is only used for month settings. Remove if weekly.
    bysetpos:
      strategy.useBysetpos &&
      options.cadence === 30 &&
      options.dayOrder !== undefined
        ? [options.dayOrder]
        : undefined,
  };

  // The startDate is an optional because the repeat dialog on the planner
  // templates doesn't have a start date.
  if (options.startDate) {
    const startDate = new Date(options.startDate * 1000);
    params.dtstart = datetime(
      startDate.getFullYear(),
      startDate.getMonth() + 1,
      startDate.getDate(),
      startDate.getHours(),
      startDate.getMinutes(),
      0,
    );
  }

  const { endingType, endingDate } = options;

  // If there is not any ending type, the repeat amount should be used
  if (endingType === undefined || endingType === "finish_after") {
    params.count = options.repeatAmount;
  } else if (endingType === "finish_on" && endingDate) {
    // If this is a specific date, it should be set
    const endDate = new Date(endingDate * 1000);
    params.until = datetime(
      endDate.getUTCFullYear(),
      endDate.getUTCMonth() + 1,
      endDate.getUTCDate(),
      endDate.getUTCHours(),
      endDate.getUTCMinutes(),
      0,
    );
  }

  return new RRule(params);
};
